import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { VideoRenderProvider } from "./context/VideoRenderContext";
import { Toaster } from "./components/ui/sonner";
import { SupportChatWidget } from "./components/SupportChatWidget";
import { InAppHelpChatWidget } from "./components/InAppHelpChatWidget";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { GeneratorPage } from "./pages/GeneratorPage";
import { PostsPage } from "./pages/PostsPage";
import { ExportPage } from "./pages/ExportPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { CalendarPage } from "./pages/CalendarPage";
import { OrganizationsPage } from "./pages/OrganizationsPage";
import { OrganizationDetailsPage } from "./pages/OrganizationDetailsPage";
import { InviteAcceptPage } from "./pages/InviteAcceptPage";
import { ClientPortalPage } from "./pages/ClientPortalPage";
import { MobileVideoCapturePage } from "./pages/MobileVideoCapturePage";
import { VideoLibraryPage } from "./pages/VideoLibraryPage";
import { AffiliatePage } from "./pages/AffiliatePage";
import { ConnectionsPage } from "./pages/ConnectionsPage";
import { OAuthCallbackPage } from "./pages/OAuthCallbackPage";
import { BlogGeneratorPage } from "./pages/BlogGeneratorPage";
import { AdGeneratorPage } from "./pages/AdGeneratorPage";
import { EmailGeneratorPage } from "./pages/EmailGeneratorPage";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminTutorialScriptsPage } from "./pages/AdminTutorialScriptsPage";
import { CreativeStudioPage } from "./pages/CreativeStudioPage";
import { BrandKitPage } from "./pages/BrandKitPage";
import { PrivacyPolicyPage, TermsOfServicePage, DataDeletionPage } from "./pages/LegalPages";
import { TwoFAChallengePage } from "./pages/TwoFAChallengePage";
import { VerifyEmailPage, VerifyEmailPendingPage } from "./pages/VerifyEmailPage";
import { ArchivePage } from "./pages/ArchivePage";
import { AIPostManagerPage } from "./pages/AIPostManagerPage";
import { ClientsPage } from "./pages/ClientsPage";
import { PricingPage } from "./pages/PricingPage";
import { CompletePaymentPage } from "./pages/CompletePaymentPage";
import { TutorialsPage } from "./pages/TutorialsPage";
import { ChangelogPage } from "./pages/ChangelogPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { ComposePage } from "./pages/ComposePage";
import { SupportPage } from "./pages/SupportPage";
import { StyleProfilesPage } from "./pages/StyleProfilesPage";
import { UrlGeneratorPage } from "./pages/UrlGeneratorPage";
import { ReviewPage } from "./pages/ReviewPage";
import { WeeklyThemesPage } from "./pages/WeeklyThemesPage";
import { CommentAutomationPage } from "./pages/CommentAutomationPage";
import { FunnelBuilderPage } from "./pages/FunnelBuilderPage";
import { FunnelLandingPage } from "./pages/FunnelLandingPage";
import { EmailDashboardPage } from "./pages/email/EmailDashboardPage";
import { EmailProvidersPage } from "./pages/email/EmailProvidersPage";
import { EmailSubscribersPage } from "./pages/email/EmailSubscribersPage";
import { EmailListsPage } from "./pages/email/EmailListsPage";
import { EmailCampaignsPage } from "./pages/email/EmailCampaignsPage";
import { EmailCampaignEditorPage } from "./pages/email/EmailCampaignEditorPage";
import { EmailCampaignStatsPage } from "./pages/email/EmailCampaignStatsPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { ProposalsPage } from "./pages/ProposalsPage";
import { ProposalBuilderPage } from "./pages/ProposalBuilderPage";
import { PublicProposalPage } from "./pages/PublicProposalPage";
import { SSOLoginPage } from "./pages/SSOLoginPage";
import { GoogleAuthCallback } from "./pages/GoogleAuthCallback";
import { UpgradeModal } from "./components/UpgradeModal";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { DashboardLayout } from "./components/DashboardLayout";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Read-only client-portal accounts live entirely in /portal. Let the OAuth
  // callback through so they can finish connecting their accounts.
  if (
    user.is_client_portal
    && !location.pathname.startsWith('/portal')
    && !location.pathname.startsWith('/connections/callback')
  ) {
    return <Navigate to="/portal" replace />;
  }
  // Block app access until email is verified (admin bootstrap user is exempt — created with email_verified=true)
  if (user.email_verified === false) {
    return <Navigate to="/verify-email-pending" replace />;
  }
  // Signup-payment gate: if the user picked a paid plan on the pricing page,
  // they MUST complete Stripe checkout (or explicitly opt out to free) before
  // entering the app. Admins and users already on a paid tier are exempt.
  if (
    user.pending_signup_plan
    && !user.is_admin
    && (user.subscription_tier || 'free') === 'free'
    && location.pathname !== '/complete-payment'
    && location.pathname !== '/subscription/success'
  ) {
    return <Navigate to="/complete-payment" replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
};

const ClientPortalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_client_portal) return <Navigate to="/dashboard" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
};

// Auth-required, but does NOT wrap the page in <DashboardLayout> so the
// signup-payment screen stays focused on conversion (no sidebar/nav).
const CompletePaymentGate = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) return <Navigate to="/verify-email-pending" replace />;
  // If they no longer have a pending plan (already paid or skipped) → dashboard
  if (!user.pending_signup_plan) return <Navigate to="/dashboard" replace />;
  return <CompletePaymentPage />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Intercept the Google OAuth return (#session_id=...) BEFORE any route guard
  // runs — the callback exchanges the session and then redirects appropriately.
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/2fa-challenge" element={<TwoFAChallengePage />} />
      {/* Central SSO login — hosted IdP page for sibling apps. Must be
          reachable whether or not the user is already signed in (it handles
          both the "continue as" fast-path and the login form). */}
      <Route path="/sso/login" element={<SSOLoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
      {/* Signup-payment gate — shown after registration when the user picked a
          paid plan on the pricing page. Bypasses the DashboardLayout (no
          sidebar) so it stays focused on the conversion. */}
      <Route path="/complete-payment" element={<CompletePaymentGate />} />

      {/* Public legal pages — accessible without auth (required for Meta app review) */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/pricing" element={<PricingPage />} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/generate" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
      <Route path="/studio/blog" element={<ProtectedRoute><BlogGeneratorPage /></ProtectedRoute>} />
      <Route path="/studio/ad" element={<ProtectedRoute><AdGeneratorPage /></ProtectedRoute>} />
      <Route path="/studio/email" element={<ProtectedRoute><EmailGeneratorPage /></ProtectedRoute>} />
      <Route path="/studio/creative" element={<ProtectedRoute><CreativeStudioPage /></ProtectedRoute>} />
      <Route path="/video-studio" element={<ProtectedRoute><VideoLibraryPage /></ProtectedRoute>} />
      <Route path="/brand-kit" element={<ProtectedRoute><BrandKitPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/posts" element={<ProtectedRoute><PostsPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/archive" element={<ProtectedRoute><ArchivePage /></ProtectedRoute>} />
      <Route path="/ai-manager" element={<ProtectedRoute><AIPostManagerPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/organizations" element={<ProtectedRoute><OrganizationsPage /></ProtectedRoute>} />
      <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationDetailsPage /></ProtectedRoute>} />
      <Route path="/invites/:token" element={<InviteAcceptPage />} />
      <Route path="/m/video/:token" element={<MobileVideoCapturePage />} />
      <Route path="/portal" element={<ClientPortalRoute><ClientPortalPage /></ClientPortalRoute>} />
      <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
      <Route path="/connections/callback/:platform" element={<ProtectedRoute><OAuthCallbackPage /></ProtectedRoute>} />
      <Route path="/export" element={<ProtectedRoute><ExportPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><AffiliatePage /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ProposalsPage /></ProtectedRoute>} />
      <Route path="/proposals/new" element={<ProtectedRoute><ProposalBuilderPage /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute><ProposalBuilderPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/tutorials" element={<ProtectedRoute><TutorialsPage /></ProtectedRoute>} />
      <Route path="/changelog" element={<ProtectedRoute><ChangelogPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/compose" element={<ProtectedRoute><ComposePage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/support/:ticketId" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/style-profiles" element={<ProtectedRoute><StyleProfilesPage /></ProtectedRoute>} />
      <Route path="/url-generator" element={<ProtectedRoute><UrlGeneratorPage /></ProtectedRoute>} />
      <Route path="/review/:token" element={<ReviewPage />} />
      <Route path="/proposal/:token" element={<PublicProposalPage />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/p/:slug" element={<FunnelLandingPage />} />
      <Route path="/weekly-themes" element={<ProtectedRoute><WeeklyThemesPage /></ProtectedRoute>} />
      <Route path="/comment-automation" element={<ProtectedRoute><CommentAutomationPage /></ProtectedRoute>} />
      <Route path="/funnels" element={<ProtectedRoute><FunnelBuilderPage /></ProtectedRoute>} />

      {/* Email Marketing */}
      <Route path="/email" element={<ProtectedRoute><EmailDashboardPage /></ProtectedRoute>} />
      <Route path="/email/providers" element={<ProtectedRoute><EmailProvidersPage /></ProtectedRoute>} />
      <Route path="/email/subscribers" element={<ProtectedRoute><EmailSubscribersPage /></ProtectedRoute>} />
      <Route path="/email/lists" element={<ProtectedRoute><EmailListsPage /></ProtectedRoute>} />
      <Route path="/email/campaigns" element={<ProtectedRoute><EmailCampaignsPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/new" element={<ProtectedRoute><EmailCampaignEditorPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/:id/edit" element={<ProtectedRoute><EmailCampaignEditorPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/:id/stats" element={<ProtectedRoute><EmailCampaignStatsPage /></ProtectedRoute>} />

      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/tutorial-scripts" element={<AdminRoute><AdminTutorialScriptsPage /></AdminRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Render the floating AI support chat widget only on public marketing /
 *  pre-auth pages. Inside the authenticated app users already have ticket
 *  support via /support, so the widget would be redundant + distracting. */
function PublicSupportChatGate() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const PUBLIC_PATHS = new Set([
    '/', '/login', '/register', '/forgot-password', '/pricing',
    '/privacy', '/terms', '/data-deletion',
  ]);
  if (user) return null;             // any signed-in user — skip
  if (!PUBLIC_PATHS.has(pathname)) return null;
  return <SupportChatWidget />;
}

/** Render the in-app help chat widget on authenticated app pages. Skips
 *  routes where a floating widget would be intrusive — admin panel,
 *  tutorials (it's already on tutorials!), and OAuth callbacks. */
function InAppHelpChatGate() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user) return null;
  // Skip these to avoid stacking widgets or interrupting flows
  const SKIP_PREFIXES = ['/admin', '/tutorials', '/connections/callback', '/invites/'];
  if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return <InAppHelpChatWidget />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VideoRenderProvider>
          <AppRoutes />
          <PublicSupportChatGate />
          <InAppHelpChatGate />
          <UpgradeModal />
          <PWAInstallPrompt />
          <Toaster position="top-right" richColors />
        </VideoRenderProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
